/**
 * RewardsService — API client for svc-lux-loyalty classic rewards endpoints.
 *
 * Covers admin CRUD (catalogs, catalog entries, inventory) used by the
 * Classic Rewards configuration page in the Flights section.
 */
import type { actions, paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { json_headers, request } from './common';

/** Base path shared with LuxLoyaltyService — both target svc-lux-loyalty. */
const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

// ---------------------------------------------------------------------------
// Response type helpers — extracted from the contract paths
// ---------------------------------------------------------------------------

/** Response type for listing catalogs. */
type ListCatalogsResponse =
  paths['/api/lux-loyalty/rewards/catalogs']['get']['responses']['200']['content']['application/json'];

/** Response type for creating a catalog. */
type CreateCatalogResponse =
  paths['/api/lux-loyalty/rewards/catalogs']['post']['responses']['201']['content']['application/json'];

/** Request body for creating a catalog. */
type CreateCatalogPayload = actions['createRewardsCatalog']['requestBody']['content']['application/json'];

/** Response type for updating a catalog status. */
type UpdateCatalogStatusResponse =
  paths['/api/lux-loyalty/rewards/catalogs/{catalogId}']['patch']['responses']['200']['content']['application/json'];

/** Request body for updating a catalog status. */
type UpdateCatalogStatusPayload = actions['updateRewardsCatalogStatus']['requestBody']['content']['application/json'];

/** Response type for querying entries. */
type QueryEntriesResponse = actions['queryRewardsEntries']['responses']['200']['content']['application/json'];

/** Request body for querying entries. */
type QueryEntriesPayload = actions['queryRewardsEntries']['requestBody']['content']['application/json'];

/** Response type for bulk upserting entries. */
type BulkUpsertEntriesResponse = actions['bulkUpsertRewardsEntries']['responses']['200']['content']['application/json'];

/** Request body for bulk upserting entries. */
type BulkUpsertEntriesPayload = actions['bulkUpsertRewardsEntries']['requestBody']['content']['application/json'];

/** Response type for updating a single entry. */
type UpdateEntryResponse = actions['updateRewardsEntry']['responses']['200']['content']['application/json'];

/** Request body for updating a single entry. */
type UpdateEntryPayload = actions['updateRewardsEntry']['requestBody']['content']['application/json'];

/** Response type for querying inventory. */
type QueryInventoryResponse = actions['queryRewardsInventory']['responses']['200']['content']['application/json'];

/** Response type for bulk upserting inventory. */
type BulkUpsertInventoryResponse =
  actions['bulkUpsertRewardsInventory']['responses']['200']['content']['application/json'];

/** Request body for bulk upserting inventory. */
type BulkUpsertInventoryPayload = actions['bulkUpsertRewardsInventory']['requestBody']['content']['application/json'];

// ---------------------------------------------------------------------------
// Exported entity types — derived from API responses for use in components
// ---------------------------------------------------------------------------

/** A rewards catalog record. */
export type RewardsCatalog = ListCatalogsResponse['result'][number];

/**
 * A catalog entry with pricing.
 *
 * Note: `cashPrice` comes from the API as a string (Prisma Decimal serialization).
 * We normalize it to a number in the service functions below.
 */
export type RawRewardsCatalogEntry = QueryEntriesResponse['result'][number];

/** Normalized catalog entry — cashPrice as number. */
export type RewardsCatalogEntry = Omit<RawRewardsCatalogEntry, 'cashPrice'> & {
  cashPrice: number;
};

type QueryRewardsInventoryRow = QueryInventoryResponse['result'][number];
type BulkUpsertInventoryTierPayload = BulkUpsertInventoryPayload['tiers'][number];

/**
 * Tier enum returned by the loyalty contract for per-customer inventory rows.
 *
 * The admin flow also models a synthetic `total` row used to cap aggregate
 * redemptions across all tiers for a catalog entry.
 */
export type RewardsTier = QueryRewardsInventoryRow['tier'] | 'total';

/** Inventory row per tier for a catalog entry, including the synthetic total row. */
export type RewardsInventoryRow = Omit<QueryRewardsInventoryRow, 'tier'> & {
  tier: RewardsTier;
};

/** Bulk upsert payload for inventory rows, including the synthetic total row. */
export type BulkUpsertRewardsInventoryPayload = Omit<BulkUpsertInventoryPayload, 'tiers'> & {
  tiers: Array<Omit<BulkUpsertInventoryTierPayload, 'tier'> & { tier: RewardsTier }>;
};

/** Catalog entry status. */
export type RewardsCatalogEntryStatus = RawRewardsCatalogEntry['status'];

/** Catalog status. */
export type RewardsCatalogStatus = RewardsCatalog['status'];

// ---------------------------------------------------------------------------
// Normalizer — converts Prisma Decimal strings to numbers
// ---------------------------------------------------------------------------

/** Converts cashPrice from Decimal string to number. */
function normalizeEntry(raw: RawRewardsCatalogEntry): RewardsCatalogEntry {
  return {
    ...raw,
    cashPrice: Number(raw.cashPrice),
  };
}

// ---------------------------------------------------------------------------
// Catalog APIs
// ---------------------------------------------------------------------------

/** Fetches reward catalogs, optionally filtered by itemType and status. */
export async function fetchCatalogs(filters?: {
  itemType?: string;
  status?: RewardsCatalogStatus;
}): Promise<Array<RewardsCatalog>> {
  const params = new URLSearchParams();
  if (filters?.itemType) {
    params.set('itemType', filters.itemType);
  }
  if (filters?.status) {
    params.set('status', filters.status);
  }

  const qs = params.toString();
  const url = `${BASE_PATH}/rewards/catalogs${qs ? `?${qs}` : ''}`;
  const response: ListCatalogsResponse = await request(url, { method: 'GET' });
  return response.result;
}

/** Creates a new rewards catalog. */
export async function createCatalog(data: CreateCatalogPayload): Promise<CreateCatalogResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalogs`;
  const response: CreateCatalogResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result;
}

/** Updates a catalog's status (ACTIVE, INACTIVE, ARCHIVED). */
export async function updateCatalogStatus(
  catalogId: string,
  data: UpdateCatalogStatusPayload,
): Promise<UpdateCatalogStatusResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalogs/${catalogId}`;
  const response: UpdateCatalogStatusResponse = await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return response.result;
}

// ---------------------------------------------------------------------------
// Entry APIs
// ---------------------------------------------------------------------------

/** Queries catalog entries by catalogId, itemType, itemSubType, and optional itemReferences. */
export async function queryEntries(
  filters: QueryEntriesPayload,
  options?: { signal?: AbortSignal },
): Promise<Array<RewardsCatalogEntry>> {
  const url = `${BASE_PATH}/rewards/catalog-entries/query`;
  const response: QueryEntriesResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(filters),
    signal: options?.signal,
  });
  return response.result.map(normalizeEntry);
}

/** Bulk upserts catalog entries (create or update by unique key). */
export async function bulkUpsertEntries(
  payload: BulkUpsertEntriesPayload,
): Promise<BulkUpsertEntriesResponse['result']> {
  const url = `${BASE_PATH}/rewards/catalog-entries/bulk-upsert`;
  const response: BulkUpsertEntriesResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response.result;
}

/** Updates a single catalog entry's pricing or status. */
export async function updateEntry(entryId: string, data: UpdateEntryPayload): Promise<RewardsCatalogEntry> {
  const url = `${BASE_PATH}/rewards/catalog-entries/${entryId}`;
  const response: UpdateEntryResponse = await request(url, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
  return normalizeEntry(response.result);
}

// ---------------------------------------------------------------------------
// Inventory APIs
// ---------------------------------------------------------------------------

/** Queries inventory rows by catalogEntryId, catalogId, and/or tier. */
export async function queryInventory(filters: {
  catalogEntryId?: string;
  catalogId?: string;
  tier?: string;
}): Promise<Array<RewardsInventoryRow>> {
  const params = new URLSearchParams();
  if (filters.catalogEntryId) {
    params.set('catalogEntryId', filters.catalogEntryId);
  }
  if (filters.catalogId) {
    params.set('catalogId', filters.catalogId);
  }
  if (filters.tier) {
    params.set('tier', filters.tier);
  }

  const qs = params.toString();
  const url = `${BASE_PATH}/rewards/inventory${qs ? `?${qs}` : ''}`;
  const response: QueryInventoryResponse = await request(url, { method: 'GET' });
  return response.result;
}

/** Bulk upserts inventory tiers for a catalog entry. */
export async function bulkUpsertInventory(
  payload: BulkUpsertRewardsInventoryPayload,
): Promise<BulkUpsertInventoryResponse['result']> {
  const url = `${BASE_PATH}/rewards/inventory/bulk-upsert`;
  const response: BulkUpsertInventoryResponse = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(payload),
  });
  return response.result;
}
